<template>
  <div>
    <b-row class="mx-0-5 my-1" align-h="between" align-v="center">
      <b-col cols="12" md="6">
        <h4>Scheduled Reports</h4>
      </b-col>
      <b-col cols="12" md="6">
        <div class="d-flex align-items-center justify-content-end">
          <SearchBar class="mr-1">
            <template #input>
              <b-form-input v-model="filter" type="search" placeholder="Search" />
            </template>
          </SearchBar>
        </div>
      </b-col>
    </b-row>
    <b-table
      responsive
      selectable
      striped
      primary-key="id"
      :items="schedules"
      :fields="tableFields"
      :per-page="perPage"
      :current-page="currentPage"
      :filter="filter"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      @filtered="onFiltered"
      table-class="text-nowrap"
      class="table-with-actions-min-height"
    >
      <template #cell(nextTrigger)="row">
        <span
          v-b-tooltip.hover
          :title="row.item.cronExpr"
        >
          {{ formatNextTrigger(row.value) }}
        </span>
      </template>
      <template #cell(actions)="row">
        <b-button
          class="btn-icon"
          variant="flat"
          v-b-tooltip.hover.variant.bottom
          title="Delete"
          @click="openDeleteModal(row.item)"
        >
          <b-icon icon="trash" />
        </b-button>
      </template>
    </b-table>
    <b-pagination
      v-model="currentPage"
      :total-rows="paginationTotalRows"
      :per-page="perPage"
      align="right"
      first-number
      last-number
      prev-class="prev-item"
      next-class="next-item"
      class="mb-1 mr-1"
    />
    <b-modal
      ref="deleteScheduleModal"
      title="Delete Scheduled Report"
      ok-title="Delete"
      ok-variant="danger"
      :ok-disabled="isDeleting"
      @ok="confirmDelete"
      @hidden="onModalHidden"
    >
      <p class="mb-0">
        Are you sure you want to delete scheduled report
        <b>{{ deleteCandidateName }}</b>?
      </p>
    </b-modal>
  </div>
</template>

<script>
import moment from 'moment';

import SearchBar from '@/xvisor/components/SearchBar.vue';
import momentTimeFormat from '@/xvisor/constants/momentTimeFormat';

export default {
  components: {
    SearchBar,
  },
  props: {
    refreshToken: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      schedules: [],
      sortBy: 'nextTrigger',
      sortDesc: false,
      tableFields: [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'type', label: 'Type', sortable: true },
        { key: 'nextTrigger', label: 'Next Run/Trigger', sortable: true },
        { key: 'actions', label: 'Actions' },
      ],
      currentPage: 1,
      perPage: 10,
      filter: null,
      filteredLength: null,
      deleteCandidate: null,
      isDeleting: false,
    };
  },
  computed: {
    paginationTotalRows() {
      if (this.filter) return this.filteredLength;
      return this.schedules.length;
    },
    deleteCandidateName() {
      return this.deleteCandidate ? this.deleteCandidate.name : '';
    },
  },
  watch: {
    refreshToken() {
      this.httpGet();
    },
  },
  mounted() {
    this.httpGet();
  },
  methods: {
    onFiltered(filteredItems) {
      this.filteredLength = filteredItems.length;
      this.currentPage = 1;
    },
    formatNextTrigger(nextTrigger) {
      if (!nextTrigger) {
        return 'Unable to compute';
      }
      return moment(nextTrigger).format(momentTimeFormat.dateTime);
    },
    openDeleteModal(schedule) {
      this.deleteCandidate = {
        id: schedule.id,
        name: schedule.name,
      };
      this.$refs.deleteScheduleModal.show();
    },
    onModalHidden() {
      if (!this.isDeleting) {
        this.deleteCandidate = null;
      }
    },
    confirmDelete(event) {
      event.preventDefault();
      if (!this.deleteCandidate || this.isDeleting) {
        return;
      }

      const candidate = this.deleteCandidate;
      this.isDeleting = true;
      this.$http
        .delete(`/reports/schedules/${candidate.id}`)
        .then(() => {
          this.$bvToast.toast(`Scheduled report "${candidate.name}" deleted.`, { variant: 'success' });
          this.$refs.deleteScheduleModal.hide();
          this.deleteCandidate = null;
          return this.httpGet();
        })
        .catch(() => {
          this.$bvToast.toast(`Failed to delete scheduled report "${candidate.name}".`, { variant: 'danger' });
        })
        .finally(() => {
          this.isDeleting = false;
        });
    },
    httpGet() {
      this.$http
        .get('/reports/schedules/list.json')
        .then((response) => { this.schedules = response.data; })
        .catch(() => {
          this.schedules = [];
          this.$bvToast.toast('Failed to load scheduled reports.', { variant: 'danger' });
        });
    },
  },
};
</script>
